import type { SsoProviderFormData } from './SsoProviderStyles';
import { useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@mui/material';
import * as Yup from 'yup';
import { Dialog } from '@components/Dialog/Dialog';
import { Button } from '@components/buttons/Button';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { useStore } from '@stores/RootStore';
import { useCreateSsoProvider } from '@queryHooks/useTenantSsoProvider';
import { FormBody, FieldsContainer, FieldRow, RequiredLabel, OptionalLabel, ModalButtonBox } from './SsoProviderStyles';

type Props = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
};

export const SsoProviderCreateModal = ({ open, onClose, tenantId }: Props) => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();

  const { mutate: createProvider, isPending } = useCreateSsoProvider();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        providerName: Yup.string().required(t('SsoProvider.create.providerNameRequired')).max(255).trim(),
        issuerUri: Yup.string()
          .required(t('SsoProvider.create.issuerUriRequired'))
          .url(t('SsoProvider.create.invalidUrl'))
          .trim(),
        clientId: Yup.string().required(t('SsoProvider.create.clientIdRequired')).trim(),
        clientSecret: Yup.string().required(t('SsoProvider.create.clientSecretRequired')).trim(),
        redirectUri: Yup.string()
          .matches(/^https?:\/\/.+/, { message: t('SsoProvider.create.invalidUrl'), excludeEmptyString: true })
          .trim(),
        scopes: Yup.string().trim(),
        userNameAttribute: Yup.string().trim(),
        userInfoUri: Yup.string()
          .matches(/^https?:\/\/.+/, { message: t('SsoProvider.create.invalidUrl'), excludeEmptyString: true })
          .trim(),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SsoProviderFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      providerName: '',
      issuerUri: '',
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      scopes: 'openid,profile,email',
      userNameAttribute: 'email',
      userInfoUri: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleValid: SubmitHandler<SsoProviderFormData> = formData => {
    createProvider(
      {
        tenantId,
        dto: {
          providerName: formData.providerName.trim(),
          issuerUri: formData.issuerUri.trim(),
          clientId: formData.clientId.trim(),
          clientSecret: formData.clientSecret.trim(),
          redirectUri: formData.redirectUri?.trim() || undefined,
          scopes: formData.scopes?.trim() || undefined,
          userNameAttribute: formData.userNameAttribute?.trim() || undefined,
          userInfoUri: formData.userInfoUri?.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toastStore.open(t('SsoProvider.create.success'), 'bottom', 'center');
          handleClose();
        },
        onError: () => {
          toastStore.open(t('SsoProvider.create.fail'), 'bottom', 'center');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t('SsoProvider.create.title')} size="medium" useCustomContent>
      <FormBody onSubmit={handleSubmit(handleValid)}>
        <FieldsContainer>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.providerName')}</RequiredLabel>
            <TextField
              {...register('providerName')}
              placeholder={t('SsoProvider.create.providerNamePlaceholder')}
              size="small"
              fullWidth
              error={!!errors.providerName}
              helperText={errors.providerName?.message}
            />
          </FieldRow>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.issuerUri')}</RequiredLabel>
            <TextField
              {...register('issuerUri')}
              placeholder="https://example.okta.com/oauth2/default"
              size="small"
              fullWidth
              error={!!errors.issuerUri}
              helperText={errors.issuerUri?.message}
            />
          </FieldRow>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.clientId')}</RequiredLabel>
            <TextField
              {...register('clientId')}
              placeholder={t('SsoProvider.create.clientIdPlaceholder')}
              size="small"
              fullWidth
              error={!!errors.clientId}
              helperText={errors.clientId?.message}
            />
          </FieldRow>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.clientSecret')}</RequiredLabel>
            <TextField
              {...register('clientSecret')}
              placeholder={t('SsoProvider.create.clientSecretPlaceholder')}
              size="small"
              fullWidth
              type="password"
              error={!!errors.clientSecret}
              helperText={errors.clientSecret?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.redirectUri')}</OptionalLabel>
            <TextField
              {...register('redirectUri')}
              placeholder="https://example.com/login"
              size="small"
              fullWidth
              error={!!errors.redirectUri}
              helperText={errors.redirectUri?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.scopes')}</OptionalLabel>
            <TextField
              {...register('scopes')}
              placeholder="openid,profile,email"
              size="small"
              fullWidth
              error={!!errors.scopes}
              helperText={errors.scopes?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.userNameAttribute')}</OptionalLabel>
            <TextField
              {...register('userNameAttribute')}
              placeholder="email"
              size="small"
              fullWidth
              error={!!errors.userNameAttribute}
              helperText={errors.userNameAttribute?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.userInfoUri')}</OptionalLabel>
            <TextField
              {...register('userInfoUri')}
              placeholder="https://example.okta.com/oauth2/default/v1/userinfo"
              size="small"
              fullWidth
              error={!!errors.userInfoUri}
              helperText={errors.userInfoUri?.message}
            />
          </FieldRow>
        </FieldsContainer>
        <ModalButtonBox>
          <SecureSubmitButton isPending={isPending} type="submit" variant="contain" size="extraLarge">
            {t('SsoProvider.create.submit')}
          </SecureSubmitButton>
          <Button variant="outline" size="extraLarge" onClick={handleClose}>
            {t('Cancel')}
          </Button>
        </ModalButtonBox>
      </FormBody>
    </Dialog>
  );
};
